import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Flex, Text } from '@oliasoft-open-source/react-ui-library';
import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';
import { syncDesigns } from 'client/store/entities/hierarchy/design/design';
import {
  fetchDesignSyncStatus, selectDesignSyncStatus, setDesignSyncing, syncStartedBatch,
} from 'client/store/entities/hierarchy/design/design-sync-status';
import { getDesignById, getDesignSyncStatus } from 'client/store/entities/hierarchy/design/selectors';
import {
  fetchDesignSyncCandidates, getDesignSyncCandidates,
} from 'client/store/entities/hierarchy/design/design-sync-candidates';
import translations from 'client/internationalization/translation-map.json';
import { useTranslation } from 'react-i18next';
import { SyncDesignModal } from './sync-designs-modal';
import { getLastPushedLabel, isDesignAncestorLevel } from './design-sync.utils';
import styles from './design-sync-controls.module.css'

type DesignSyncControlsProps = {
  activeItem: { id: string; entityType: HierarchyLevelType } | null,
};

export const DesignSyncControls = ({ activeItem }: DesignSyncControlsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [syncModalVisible, setSyncModalVisible] = useState(false);

  const isDesignLevel = activeItem?.entityType === HierarchyLevelType.Design;
  const ancestorLevel = activeItem && isDesignAncestorLevel(activeItem.entityType) ? activeItem.entityType : null;
  const activeDesignId = isDesignLevel ? activeItem.id : null;

  const isSyncing = useSelector(selectDesignSyncStatus(activeDesignId ?? ''));
  const designSyncStatus = useSelector(getDesignSyncStatus);
  const { items: designsUnderActiveItem, isLoading: isLoadingDesigns } = useSelector(state => getDesignSyncCandidates(state, activeItem?.id ?? ''));
  const activeDesign = useSelector(state => getDesignById(state, activeDesignId ?? ''));

  useEffect(() => {
    if (activeItem && ancestorLevel) {
      dispatch(fetchDesignSyncCandidates(ancestorLevel, activeItem.id));
    }
  }, [activeItem?.id, ancestorLevel]);

  useEffect(() => {
    if (activeDesignId) {
      dispatch(fetchDesignSyncStatus([activeDesignId]));
    }
  }, [activeDesignId]);

  useEffect(() => {
    if (designsUnderActiveItem.length) {
      dispatch(fetchDesignSyncStatus(designsUnderActiveItem.map(design => design.designid)));
    }
  }, [designsUnderActiveItem]);

  if (!isDesignLevel && !ancestorLevel) return null;

  const isSyncingAllDesigns = designsUnderActiveItem.length > 0 && designsUnderActiveItem.every(design => designSyncStatus[design.designid]);
  const lastPushedLabel = getLastPushedLabel(t, activeDesign?.last_synced_out_at);

  const startSyncDesign = () => {
    if (!activeDesignId) return;
    dispatch(setDesignSyncing(activeDesignId));
    dispatch(syncDesigns([activeDesignId]));
  };

  const startSyncDesigns = (designids: string[]) => {
    dispatch(syncStartedBatch(Object.fromEntries(designids.map(designid => [designid, true]))));
    dispatch(syncDesigns(designids));
  };

  const openSyncModal = () => {
    setSyncModalVisible(true);
  };

  const closeSyncModal = () => {
    setSyncModalVisible(false);
  };

  return (
    <div className={styles.designSyncControls}>
      {syncModalVisible && ancestorLevel && (
        <SyncDesignModal
          entityType={ancestorLevel}
          onClose={closeSyncModal}
          onConfirmSyncStartSelect={startSyncDesigns}
          designs={designsUnderActiveItem}
          isLoading={isLoadingDesigns}
          designSyncStatuses={designSyncStatus}
        />
      )}
      <Flex alignItems="center" gap="var(--padding-xs)">
        {isDesignLevel && (
          <>
            {lastPushedLabel && <Text muted>{lastPushedLabel}</Text>}
            <Button onClick={startSyncDesign} icon={isSyncing ? undefined : 'sync'} colored label={t(translations.design_syncCurrentDesign)} disabled={isSyncing} loading={isSyncing} />
          </>
        )}
        {ancestorLevel && (
          <Button onClick={openSyncModal} icon={isSyncingAllDesigns ? undefined : 'sync'} colored label={t(translations.design_syncAllDesigns)} disabled={!designsUnderActiveItem.length || isSyncingAllDesigns} loading={isSyncingAllDesigns} />
        )}
      </Flex>
    </div>
  );
};
